import * as React from "react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ChevronLeftIcon, ChevronRightIcon, MoreHorizontalIcon } from "lucide-react";

function range(start: number, end: number): number[] {
  const length = Math.max(0, end - start + 1);
  return Array.from({ length }, (_, i) => start + i);
}

function getPaginationItems(page: number, totalPages: number, boundaryRange: number): Array<number | "ellipsis"> {
  const siblingCount = 1;
  const startPages = range(1, Math.min(boundaryRange, totalPages));
  const endPages = range(Math.max(1, totalPages - boundaryRange + 1), totalPages);
  const aroundCurrent = range(Math.max(1, page - siblingCount), Math.min(totalPages, page + siblingCount));

  const allPages = new Set([...startPages, ...aroundCurrent, ...endPages]);
  const sortedPages = Array.from(allPages).sort((a, b) => a - b);

  const result: Array<number | "ellipsis"> = [];
  let prev = 0;
  for (const p of sortedPages) {
    if (p > prev + 1) {
      result.push("ellipsis");
    }
    result.push(p);
    prev = p;
  }
  return result;
}

type PaginationProps = React.ComponentProps<"nav"> & {
  page?: number;
  totalPages?: number;
  boundaryRange?: number;
  onPageChange?: (page: number) => void;
  hidePrevious?: boolean;
  hideNext?: boolean;
  disabled?: boolean;
};

function Pagination({
  className,
  page,
  totalPages,
  boundaryRange = 1,
  onPageChange,
  hidePrevious = false,
  hideNext = false,
  disabled = false,
  children,
  ...props
}: PaginationProps) {
  const isControlled = page !== undefined && totalPages !== undefined && totalPages > 0 && onPageChange;

  if (isControlled) {
    const currentPage = Math.min(Math.max(1, page), totalPages);
    const items = getPaginationItems(currentPage, totalPages, boundaryRange);

    return (
      <nav
        role="navigation"
        aria-label="pagination"
        data-slot="pagination"
        className={cn("mx-auto flex w-full justify-center", className)}
        {...props}
      >
        <PaginationContent>
          {!hidePrevious && (
            <PaginationItem>
              <PaginationPrevious
                onClick={e => {
                  e.preventDefault();
                  if (!disabled && currentPage > 1) onPageChange(currentPage - 1);
                }}
                href="#"
                className={cn((disabled || currentPage <= 1) && "pointer-events-none opacity-50")}
                aria-disabled={disabled || currentPage <= 1}
              />
            </PaginationItem>
          )}
          {items.map((item, index) =>
            item === "ellipsis" ? (
              <PaginationItem key={`ellipsis-${index}`}>
                <PaginationEllipsis />
              </PaginationItem>
            ) : (
              <PaginationItem key={item}>
                <PaginationLink
                  isActive={item === currentPage}
                  onClick={e => {
                    e.preventDefault();
                    if (!disabled) onPageChange(item);
                  }}
                  href="#"
                  className={cn(disabled && "pointer-events-none opacity-50")}
                  aria-disabled={disabled}
                >
                  {item}
                </PaginationLink>
              </PaginationItem>
            )
          )}
          {!hideNext && (
            <PaginationItem>
              <PaginationNext
                onClick={e => {
                  e.preventDefault();
                  if (!disabled && currentPage < totalPages) onPageChange(currentPage + 1);
                }}
                href="#"
                className={cn((disabled || currentPage >= totalPages) && "pointer-events-none opacity-50")}
                aria-disabled={disabled || currentPage >= totalPages}
              />
            </PaginationItem>
          )}
        </PaginationContent>
      </nav>
    );
  }

  return (
    <nav
      role="navigation"
      aria-label="pagination"
      data-slot="pagination"
      className={cn("mx-auto flex w-full justify-center", className)}
      {...props}
    >
      {children}
    </nav>
  );
}

function PaginationContent({ className, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="pagination-content"
      className={cn("gap-0.5 flex items-center ignore-base-styling", className)}
      {...props}
    />
  );
}

function PaginationItem({ className, ...props }: React.ComponentProps<"li">) {
  return <li data-slot="pagination-item" className={cn("ignore-base-styling", className)} {...props} />;
}

type PaginationLinkProps = {
  isActive?: boolean;
} & Pick<React.ComponentProps<typeof Button>, "size"> &
  React.ComponentProps<"a">;

function PaginationLink({ className, isActive, size = "icon", ...props }: PaginationLinkProps) {
  return (
    <Button
      variant={isActive ? "outline" : "ghost"}
      size={size}
      className={cn(className)}
      nativeButton={false}
      render={
        <a aria-current={isActive ? "page" : undefined} data-slot="pagination-link" data-active={isActive} {...props} />
      }
    />
  );
}

function PaginationPrevious({
  className,
  text = "Previous",
  ...props
}: React.ComponentProps<typeof PaginationLink> & { text?: string }) {
  return (
    <PaginationLink aria-label="Go to previous page" size="default" className={cn("pl-1.5!", className)} {...props}>
      <ChevronLeftIcon data-icon="inline-start" className="cn-rtl-flip" />
      <span className="hidden sm:block">{text}</span>
    </PaginationLink>
  );
}

function PaginationNext({
  className,
  text = "Next",
  ...props
}: React.ComponentProps<typeof PaginationLink> & { text?: string }) {
  return (
    <PaginationLink aria-label="Go to next page" size="default" className={cn("pr-1.5!", className)} {...props}>
      <span className="hidden sm:block">{text}</span>
      <ChevronRightIcon data-icon="inline-end" className="cn-rtl-flip" />
    </PaginationLink>
  );
}

function PaginationEllipsis({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      aria-hidden
      data-slot="pagination-ellipsis"
      className={cn("size-8 [&_svg:not([class*='size-'])]:size-4 flex items-center justify-center", className)}
      {...props}
    >
      <MoreHorizontalIcon />
      <span className="sr-only">More pages</span>
    </span>
  );
}

Pagination.displayName = "Pagination";
PaginationContent.displayName = "PaginationContent";
PaginationEllipsis.displayName = "PaginationEllipsis";
PaginationItem.displayName = "PaginationItem";
PaginationLink.displayName = "PaginationLink";
PaginationNext.displayName = "PaginationNext";
PaginationPrevious.displayName = "PaginationPrevious";

const PaginationNamespace = Object.assign(Pagination, {
  Content: PaginationContent,
  Ellipsis: PaginationEllipsis,
  Item: PaginationItem,
  Link: PaginationLink,
  Next: PaginationNext,
  Previous: PaginationPrevious,
});

export { PaginationNamespace as Pagination };
